import type { AuthProvider } from '@refinedev/core';
import { isApiError } from '@/api/errors';
import { verifyMfa } from './auth-api';
import { clearSession, getSession } from './session';
import { landingPathFor } from './rbac';

/**
 * Refine's auth provider.
 *
 * ⚠️ `login` here is the **second** step only. Refine models sign-in as one
 * call and this one is two: a password yields an MFA challenge that grants
 * nothing (06 §3). Mapping the password step onto `login` would mean the
 * provider reporting success for a step that produced no session, so the login
 * screen drives step one itself and only the exchange of a challenge for a
 * session goes through here.
 */
export const authProvider: AuthProvider = {
  async login(params: { mfaToken?: string; code?: string }) {
    if (!params.mfaToken || !params.code) {
      return {
        success: false,
        error: {
          name: 'MFA_REQUIRED',
          message: 'Complete the password step first — a password alone grants no session.',
        },
      };
    }

    const session = await verifyMfa(params.mfaToken, params.code);
    return { success: true, redirectTo: landingPathFor(session.role) };
  },

  // eslint-disable-next-line @typescript-eslint/require-await -- Refine's interface is async
  async check() {
    const session = getSession();
    if (session) return { authenticated: true };
    return { authenticated: false, redirectTo: '/login', logout: true };
  },

  // eslint-disable-next-line @typescript-eslint/require-await
  async logout() {
    clearSession();
    return { success: true, redirectTo: '/login' };
  },

  // eslint-disable-next-line @typescript-eslint/require-await
  async getIdentity() {
    const session = getSession();
    if (!session) return null;
    // ⚠️ Identity is the admin's id and role only. The panel has no endpoint
    // that returns the signed-in admin's own name, and inventing one from the
    // token payload would put an email in every render tree — including the
    // ANALYST screens, which must have no code path that can render one.
    return { id: session.adminId, role: session.role };
  },

  // eslint-disable-next-line @typescript-eslint/require-await
  async getPermissions() {
    return getSession()?.role ?? null;
  },

  /**
   * ⚠️ A `403` is not a sign-out. The server rejecting one action because of
   * the admin's role says nothing about their session, and logging them out
   * would turn "you cannot do that" into "you have been signed out" — which
   * reads as a fault and sends someone to check the API.
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  async onError(error) {
    if (isApiError(error) && error.status === 401) {
      clearSession();
      return { logout: true, redirectTo: '/login', error };
    }
    return { error };
  },
};

export { landingPathFor };
